import { isValid, parseISO } from "date-fns";
import IMAGESIZES from "src/constants/IMAGESIZES";
import Post from "src/constants/Post";
import { BuildServerlessBookCoverImageUrl, formatImageUrl } from "helpers/Helper";

const MS_PER_MINUTE = 60 * 1000;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

export const isEbbLandingPageType = (postOrType) => {
  const postType =
    typeof postOrType === "object" && postOrType !== null ? postOrType.post_type : postOrType;
  const type = Number(postType);
  return type === Post.TYPES.EBB_LANDING_PAGE || type === Post.TYPES.EBB_LANDING_PAGE_SIMPLE;
};

export const isEbbHomepage = isEbbLandingPageType;

export const getHomepagePost = (props) =>
  props?.data?.[0]?.Post?.post || props?.data?.[0]?.Index?.post || {};

export const getLandingPagePost = getHomepagePost;

export const getPostSlug = (post) => {
  const slug = post?.slug;
  if (Array.isArray(slug)) return slug.filter(Boolean).join("/");
  return String(slug || "").replace(/^\/+|\/+$/g, "");
};

const plainTextFromOrion = (value) => {
  if (!value) return "";
  return String(value)
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;|&apos;/gi, "'")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&#(\d+);/g, (_, code) => String.fromCharCode(Number(code)))
    .replace(/&#x([0-9a-f]+);/gi, (_, hex) => String.fromCharCode(parseInt(hex, 16)))
    .replace(/[^\S\n]+/g, " ")
    .replace(/\n+/g, "\n")
    .trim();
};

const mediaName = (media) => media?.name || media?.Name || "";

const promoTypeValue = (promoType) =>
  typeof promoType === "string" ? promoType : promoType?.promoType || "";

const ribbonClass = (component) =>
  String(component?.props?.classes || "")
    .trim()
    .toLowerCase();

const isBookRibbon = (component) =>
  promoTypeValue(component?.props?.promoType) === "book" ||
  ribbonClass(component) === "book-deals" ||
  ribbonClass(component) === "book deals";

const ABOUT_ART_CLASSES = new Set(["mosaic", "about-art", "about"]);
const HERO_IMAGE_ROTATION_CLASS = "hero-image-rotation";
const USED_SIMPLE_RIBBON_CLASSES = new Set([
  "instructional",
  "retailer",
  "devices",
  "hero-devices",
  HERO_IMAGE_ROTATION_CLASS,
]);
const isHeroImageRotationRibbon = (component) =>
  ribbonClass(component) === HERO_IMAGE_ROTATION_CLASS;

const isIsbnCoverUrl = (url = "") =>
  /(?:^|\/)\d{10,13}(?:_[a-z0-9]+)?\.(?:jpe?g|png|webp)(?:\?|$)/i.test(String(url));

export const extractHomepageSignup = (post) => {
  const newsletter = (post?.components || []).find(
    (component) => component?.type === "newsletter-v2"
  );
  const props = newsletter?.props || {};
  const ribbonModal = (post?.components || []).find((component) => {
    const options = component?.props?.newsletterOptions?.props;
    return (
      component?.type === "promo-ribbon" &&
      component?.props?.button &&
      options?.subComponentType === "modal" &&
      !isBookRibbon(component)
    );
  });
  const dealsRibbonModal = (post?.components || []).find((component) => {
    const options = component?.props?.newsletterOptions?.props;
    return (
      component?.type === "promo-ribbon" &&
      isBookRibbon(component) &&
      component?.props?.button &&
      options?.subComponentType === "modal"
    );
  });
  const ribbonNewsletter = ribbonModal?.props?.newsletterOptions?.props;
  const dealsNewsletter = dealsRibbonModal?.props?.newsletterOptions?.props;
  const pageIsModal = props.subComponentType === "modal";
  const modalNewsletter = ribbonNewsletter || (pageIsModal ? props : null);

  return {
    header: plainTextFromOrion(props.header || props.modalHeader),
    subcopy: plainTextFromOrion(props.subHeader || props.newsletterSubCopy),
    modalHeader: plainTextFromOrion(
      (modalNewsletter && (modalNewsletter.modalHeader || modalNewsletter.header)) ||
        props.modalHeader ||
        props.header
    ),
    buttonText: plainTextFromOrion(
      props.buttonText ||
        ribbonNewsletter?.newsletterSignUpButtonText ||
        ribbonNewsletter?.buttonText
    ),
    signUpLocation: (props.signUpLocation || "").trim(),
    pageNewsletter: props,
    isModal: Boolean(modalNewsletter),
    newsletterProps: modalNewsletter || {},
    dealsNewsletter: dealsNewsletter || null,
    dealsModalHeader: plainTextFromOrion(dealsNewsletter?.modalHeader || dealsNewsletter?.header),
  };
};

const normalizeImageUrl = (value) => {
  if (!value || typeof value !== "string") return value || "";
  const trimmed = value.trim();
  if (!trimmed) return "";
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  if (trimmed.startsWith("//")) return `https:${trimmed}`;
  if (/^\/?[\w.-]+\.[a-z]{2,}\//i.test(trimmed)) return `https://${trimmed.replace(/^\/+/, "")}`;
  return trimmed;
};

const sizedUrl = (name, size) => {
  if (!name) return "";
  const normalized = normalizeImageUrl(name);
  if (/^https?:\/\//i.test(normalized)) return normalized;
  try {
    return formatImageUrl(name, size);
  } catch {
    return "";
  }
};

const promoImageUrls = (image) => {
  if (!image) return { desktop: "", mobile: "" };
  if (typeof image === "string") {
    const normalized = normalizeImageUrl(image);
    if (/^https?:\/\//i.test(normalized)) {
      const mdMatch = normalized.match(/\/md_(.+)$/);
      if (mdMatch) {
        return {
          desktop: sizedUrl(mdMatch[1], "lg") || normalized,
          mobile: sizedUrl(mdMatch[1], "lg") || normalized,
        };
      }
      return { desktop: normalized, mobile: normalized };
    }
    return { desktop: sizedUrl(image, "lg"), mobile: sizedUrl(image, "lg") };
  }
  const name = mediaName(image);
  return { desktop: sizedUrl(name, "lg"), mobile: sizedUrl(name, "lg") };
};

const DEFAULT_HERO_DESKTOP = "Background-Image-Large.png";
const DEFAULT_HERO_MOBILE = "Banner-Mobile.png";

const s3Image = (fileName) => {
  const name = String(fileName || "").trim();
  if (!name) return "";
  if (/^https?:\/\//i.test(name)) return name;
  const base = String(process.env.S3_IMAGE_URL || "").replace(/\/+$/, "");
  return `${base}/${name.replace(/^\/+/, "")}`;
};

export const extractHomepageMedia = ({ heroDesktopFile, heroMobileFile } = {}) => ({
  heroDesktop: s3Image(heroDesktopFile || DEFAULT_HERO_DESKTOP),
  heroMobile: s3Image(heroMobileFile || DEFAULT_HERO_MOBILE),
});

const utcDayNumber = (date) =>
  Math.floor(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()) / MS_PER_DAY);

const hasHeroRotationConfig = (props = {}) => {
  const amount = Number(String(props.rotationDays ?? "").trim());
  return Number.isFinite(amount) && amount >= 1;
};

const hasPromoImage = (card) => {
  const image = card?.image;
  if (!image) return false;
  if (typeof image === "string") return Boolean(image.trim());
  return Boolean(mediaName(image));
};

const parseRotationUnit = (value) => {
  const unit = String(value ?? "")
    .trim()
    .toLowerCase();
  if (unit === "minutes" || unit === "minute" || unit === "min") {
    return "minutes";
  }
  return "days";
};

const parseRotationAmount = (value) => {
  const amount = Number(String(value ?? "").trim());
  if (Number.isFinite(amount) && amount >= 1) {
    return Math.floor(amount);
  }
  return null;
};

const parseRotationStart = (value) => {
  const raw = String(value ?? "").trim();
  if (!raw) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    const [year, month, day] = raw.split("-").map(Number);
    return new Date(Date.UTC(year, month - 1, day));
  }
  const parsed = parseISO(raw);
  if (isValid(parsed)) return parsed;
  const fallback = new Date(raw);
  return Number.isNaN(fallback.getTime()) ? null : fallback;
};

const rotationPeriodIndex = (
  cardCount,
  { rotationDays, rotationUnit, rotationStart } = {},
  now = new Date()
) => {
  if (cardCount <= 1) return 0;
  const unit = parseRotationUnit(rotationUnit);
  const amount = parseRotationAmount(rotationDays);
  if (!amount) return 0;
  const start = parseRotationStart(rotationStart);
  const periodMs = unit === "minutes" ? amount * MS_PER_MINUTE : amount * MS_PER_DAY;
  let period;
  if (start) {
    period =
      unit === "minutes"
        ? Math.floor((now.getTime() - start.getTime()) / periodMs)
        : Math.floor((utcDayNumber(now) - utcDayNumber(start)) / amount);
  } else if (unit === "days") {
    period = Math.floor(utcDayNumber(now) / amount);
  } else {
    period = Math.floor(now.getTime() / periodMs);
  }
  if (!Number.isFinite(period)) return 0;
  return ((period % cardCount) + cardCount) % cardCount;
};

export const extractSimpleLandingMedia = (post, now = new Date()) => {
  const heroRibbon = (post?.components || []).find(
    (component) => component?.type === "promo-ribbon" && isHeroImageRotationRibbon(component)
  );
  const ribbonCards = (heroRibbon?.props?.promoCards || []).filter(hasPromoImage);
  const useRotation = ribbonCards.length > 1 && hasHeroRotationConfig(heroRibbon?.props);
  const ribbonCard = useRotation
    ? ribbonCards[rotationPeriodIndex(ribbonCards.length, heroRibbon?.props, now)]
    : ribbonCards[0];
  if (ribbonCard?.image) {
    const urls = promoImageUrls(ribbonCard.image);
    return {
      devices: urls.desktop || urls.mobile,
      alt: plainTextFromOrion(ribbonCard.text || ribbonCard.altText),
    };
  }

  const media = post?.featured_media;
  const urls = promoImageUrls(media);
  return {
    devices: urls.desktop || urls.mobile || sizedUrl(mediaName(media), "lg"),
    alt: plainTextFromOrion(media?.alt_text || media?.Alt_Text || media?.title || media?.Title),
  };
};

const cardImage = (card) => {
  if (typeof card?.image === "string") return normalizeImageUrl(card.image);
  return mediaName(card?.image);
};

const cardCopy = (card) => ({
  heading: plainTextFromOrion(card?.text),
  body: plainTextFromOrion(card?.description || card?.subtext),
});

export const extractHomepageFeatures = (post) => {
  const instructional = (post?.components || []).find(
    (component) => component?.type === "promo-ribbon" && ribbonClass(component) === "instructional"
  );
  return (instructional?.props?.promoCards || [])
    .map((card) => ({
      heading: cardCopy(card).heading,
      body: cardCopy(card).body,
      imageUrl: cardImage(card),
    }))
    .filter((card) => card.heading || card.body);
};

export const extractInstructionalSection = (post) => {
  const instructional = (post?.components || []).find(
    (component) => component?.type === "promo-ribbon" && ribbonClass(component) === "instructional"
  );
  return {
    header: plainTextFromOrion(instructional?.props?.header),
    subheader: plainTextFromOrion(instructional?.props?.subheader),
  };
};

export const extractHomepageClosing = (post) => {
  const ribbons = (post?.components || []).filter(
    (component) => component?.type === "promo-ribbon"
  );
  const closingRibbon =
    ribbons.find((component) => ribbonClass(component) === "retailer") ||
    ribbons.find(
      (component) =>
        !isBookRibbon(component) &&
        !USED_SIMPLE_RIBBON_CLASSES.has(ribbonClass(component)) &&
        !ABOUT_ART_CLASSES.has(ribbonClass(component))
    );

  if (!closingRibbon) {
    return { header: "", imageDesktop: "", imageMobile: "" };
  }

  const firstCard = (closingRibbon.props?.promoCards || []).find((card) => card?.image);
  const urls = promoImageUrls(firstCard?.image);
  return {
    header: plainTextFromOrion(closingRibbon.props?.header),
    imageDesktop: urls.desktop,
    imageMobile: urls.mobile,
  };
};

const isbnFromImageUrl = (url = "") => {
  const match = String(url).match(/(\d{10,13})/);
  return match ? match[1] : "";
};

const readCoverUrl = (affiliate = {}) => {
  const { image, isbn, primary_isbn } = affiliate;
  const imageName =
    image?.props?.image?.Name ||
    image?.props?.image?.name ||
    (typeof image === "string" ? image : "");

  if (imageName) {
    if (/^https?:\/\//i.test(imageName)) return imageName;
    return BuildServerlessBookCoverImageUrl(IMAGESIZES.BC_SEARCHRESULT, imageName);
  }

  const isbnValue = isbn || primary_isbn || "";
  if (isbnValue) {
    return BuildServerlessBookCoverImageUrl(IMAGESIZES.BC_SEARCHRESULT, `${isbnValue}.jpg`);
  }

  return "";
};

const firstCompositeCard = (ribbon) =>
  (ribbon?.props?.promoCards || []).find((card) => {
    if (!card?.image) return false;
    const urls = promoImageUrls(card.image);
    const src = urls.desktop || urls.mobile;
    return Boolean(src) && !isIsbnCoverUrl(src);
  });

const firstImageOnlyCard = (ribbon) =>
  (ribbon?.props?.promoCards || []).find((card) => {
    if (!card?.image) return false;
    const { heading, body } = cardCopy(card);
    if (heading || body) return false;
    const urls = promoImageUrls(card.image);
    const src = urls.desktop || urls.mobile;
    return Boolean(src) && !isIsbnCoverUrl(src);
  });

export const extractSimpleAboutArt = (post) => {
  const ribbons = (post?.components || []).filter(
    (component) => component?.type === "promo-ribbon"
  );
  const dedicated = ribbons.find((component) => ABOUT_ART_CLASSES.has(ribbonClass(component)));
  const instructional = ribbons.find((component) => ribbonClass(component) === "instructional");
  const unusedCustom = ribbons.find((component) => {
    const cls = ribbonClass(component);
    if (USED_SIMPLE_RIBBON_CLASSES.has(cls) || ABOUT_ART_CLASSES.has(cls)) return false;
    return Boolean(firstCompositeCard(component));
  });
  const source = dedicated || instructional || unusedCustom;
  const card =
    firstCompositeCard(dedicated) ||
    firstImageOnlyCard(instructional) ||
    firstCompositeCard(unusedCustom);

  if (!card?.image) {
    return { desktop: "", mobile: "", alt: "" };
  }

  const urls = promoImageUrls(card.image);
  if (!urls.desktop && !urls.mobile) {
    return { desktop: "", mobile: "", alt: "" };
  }

  return {
    desktop: urls.desktop,
    mobile: urls.mobile,
    alt: plainTextFromOrion(card.text || card.altText || source?.props?.header),
  };
};

export const extractHomepageTestimonials = (post) => {
  const slider = (post?.components || []).find(
    (component) => component?.type === "quote-slider" || component?.type === "inline-quotes"
  );

  return (slider?.props?.quotes || [])
    .map((quote) => ({
      text: plainTextFromOrion(quote?.text),
      author: plainTextFromOrion(quote?.author),
    }))
    .filter((quote) => quote.text);
};

export const extractHomepageDeals = (post) => {
  const dealsRibbon = (post?.components || []).find(
    (component) => component?.type === "promo-ribbon" && isBookRibbon(component)
  );

  if (!dealsRibbon) {
    return { header: "", subheader: "", books: [] };
  }

  const books = (dealsRibbon.props?.promoCards || [])
    .filter((card) => card?.image)
    .map((card) => {
      const imageUrl =
        typeof card.image === "string" ? card.image : readCoverUrl({ image: card.image });
      return {
        title: plainTextFromOrion(card.text),
        isbn: isbnFromImageUrl(imageUrl),
        price: String(card.salePrice ?? "").trim(),
        imageUrl,
        link: card.link || "",
      };
    })
    .filter((book) => book.imageUrl);

  return {
    header: plainTextFromOrion(dealsRibbon.props?.header),
    subheader: plainTextFromOrion(dealsRibbon.props?.subheader),
    books,
  };
};

export const formatBadgePrice = (price) => {
  if (price === null || price === undefined || price === "") return "";
  const cleaned = String(price).trim().replace(/^\$/, "");
  const num = Number(cleaned);
  if (Number.isNaN(num)) return cleaned.replace(/\$/g, "");
  if (num === 0) return "FREE";
  return num.toFixed(2).replace(/\.00$/, "");
};
